import {controller, target, attr} from '@github/catalyst'

interface ReplayMessage {
  id: string
  login: string
  user_id: number
  staff: boolean
  body: string
  offset: number
}

// A note the element inserts into the stream of messages at a given offset, so
// it lands in the pane at the right moment rather than whenever it was fetched.
interface ReplayNote {
  note: string
  offset: number
}

type QueuedItem = ReplayMessage | ReplayNote

function isNote(item: QueuedItem): item is ReplayNote {
  return 'note' in item
}

interface ReplayResponse {
  messages: ReplayMessage[]
  history: ReplayMessage[]
  next_from: number
  truncated: boolean
  end_of_chat: boolean
}

// Plays a finished stream's chat back in step with its VOD, the way a Twitch
// VOD does: messages appear as the playhead reaches the moment they were sent,
// and a seek rebuilds the pane around the new position.
//
// The server hands out one fixed-length window of chat at a time, keyed on
// seconds into the VOD, so this only ever holds a few seconds of buffer and a
// seek is a single request rather than a scan through the whole log.
@controller
class ChatReplayElement extends HTMLElement {
  @target declare player: HTMLElement & {currentTime: number}
  @target declare messagesContainer: HTMLElement
  @target declare chatSidebar: HTMLElement
  @target declare toggleButton: HTMLButtonElement
  @target declare messageTemplate: HTMLTemplateElement
  @target declare systemMessageTemplate: HTMLTemplateElement
  @target declare statusLine: HTMLElement

  @attr replayUrl = ''

  // Refill once the buffer runs closer than this to the playhead, so a viewer
  // watching straight through never waits on a request.
  private PREFETCH_LEAD = 10
  private MAX_RENDERED = 200
  private NAME_COLORS = [
    'text-rose-400',
    'text-emerald-400',
    'text-sky-400',
    'text-amber-400',
  ]

  // Fetched but not yet due, ascending by offset.
  private pending: QueuedItem[] = []
  // Seconds of VOD the fetches so far have covered.
  private fetchedThrough = 0
  // The playhead position the pane has been drained to.
  private lastTime = 0
  private endOfChat = false
  private loading = false
  // Bumped on every seek; a response carrying a stale generation is dropped
  // rather than poured into a pane that has since moved elsewhere.
  private generation = 0
  private seeking = false
  // Whether the viewer is reading at the bottom of the pane. Someone scrolled
  // up is reading back through chat, and shouldn't be yanked down.
  private pinned = true
  private placeholder: HTMLElement | null = null
  // A trial shift on top of the stored offset, driven by the calibration
  // screen. Windows are still requested in the stored offset's own space and
  // the results shifted back, so previewing a shift never drops the messages
  // either side of a window boundary.
  private offsetAdjust = 0

  connectedCallback() {
    // Targets (and the upgraded mux-player) aren't guaranteed to be resolved
    // yet -- same reason vod-chapter-editor waits a frame.
    requestAnimationFrame(() => this.start())
  }

  disconnectedCallback() {
    this.detachPlayer()
    this.generation++
  }

  private start() {
    if (!this.player || !this.messagesContainer) return

    this.player.addEventListener('timeupdate', this.onTimeUpdate)
    this.player.addEventListener('seeking', this.onSeeking)
    this.player.addEventListener('seeked', this.onSeeked)
    this.messagesContainer.addEventListener('scroll', this.onScroll)

    this.reset(this.currentTime())
  }

  private detachPlayer() {
    this.player?.removeEventListener('timeupdate', this.onTimeUpdate)
    this.player?.removeEventListener('seeking', this.onSeeking)
    this.player?.removeEventListener('seeked', this.onSeeked)
    this.messagesContainer?.removeEventListener('scroll', this.onScroll)
  }

  private currentTime(): number {
    const time = this.player?.currentTime ?? 0
    return Number.isFinite(time) ? Math.max(0, time) : 0
  }

  // Reads the attribute rather than the `replayUrl` accessor, for the reason
  // live-chat-element.ts spells out: Catalyst has not always installed its
  // attr accessors by the time the element first runs, and the accessor then
  // answers with the class-field default instead of the markup's value.
  private endpoint(): string {
    return this.getAttribute('data-replay-url') || this.replayUrl
  }

  // Called by the calibration screen as its control moves. Public API, not an
  // attribute, so the pane refills the moment the number changes.
  setOffsetAdjust(seconds: number) {
    if (!Number.isFinite(seconds) || seconds === this.offsetAdjust) return

    this.offsetAdjust = seconds
    this.reset(this.currentTime())
  }

  // Where a playhead position sits on the stored offset's timeline — the one
  // the server serves windows against.
  private storedPosition(displayTime: number): number {
    return displayTime - this.offsetAdjust
  }

  private onTimeUpdate = () => {
    // A hidden pane has nothing to show and nothing to buffer for.
    if (this.chatSidebar?.hidden) return

    const time = this.currentTime()

    // Playback only runs backwards through a seek. `seeked` normally catches
    // those first, but a player that reports the new position before firing it
    // would otherwise replay messages the pane is already showing.
    if (time < this.lastTime - 1) {
      this.reset(time)
      return
    }

    this.lastTime = time
    this.drain(time)
    this.maybePrefetch()
  }

  // Scrubbing fires `seeking` repeatedly and `seeked` once at the end, so the
  // pane freezes for the duration of the scrub and refills from the position
  // the viewer actually settled on.
  private onSeeking = () => {
    this.seeking = true
  }

  private onSeeked = () => {
    this.reset(this.currentTime())
  }

  private onScroll = () => {
    const container = this.messagesContainer
    this.pinned = container.scrollHeight - container.scrollTop - container.clientHeight < 40
  }

  // Rebuilds the pane around a new playhead position: everything buffered for
  // the old position is wrong, and the request that replaces it asks for
  // history too so the viewer lands in a conversation rather than a blank pane.
  private reset(time: number) {
    const generation = ++this.generation
    this.pending = []
    this.lastTime = time
    this.fetchedThrough = this.storedPosition(time)
    this.endOfChat = false
    this.seeking = false
    this.pinned = true
    this.placeholder = null
    this.messagesContainer.replaceChildren()

    this.fetchWindow(this.storedPosition(time), true, generation)
  }

  private async fetchWindow(from: number, history: boolean, generation: number) {
    this.loading = true

    try {
      let data: ReplayResponse

      // Only the request itself is guarded here. Rendering used to sit inside
      // this block too, so a bug painting the pane surfaced as "chat replay is
      // unavailable" — which reads as an outage and sends you hunting for a
      // failed request that never happened.
      try {
        const url = new URL(this.endpoint(), window.location.origin)
        url.searchParams.set('from', String(Math.max(0, from)))
        if (history) url.searchParams.set('history', '1')

        const response = await fetch(url.toString(), {headers: {Accept: 'application/json'}})
        if (!response.ok) throw new Error(`Chat replay responded ${response.status}`)

        data = await response.json()
      } catch (error) {
        // Logged rather than swallowed: the status line can only say that
        // something went wrong, and the pane is the last place left to find
        // out what.
        console.error('Chat replay could not load a window', error)
        if (generation === this.generation) this.showStatus('Chat replay is unavailable right now.')
        return
      }

      // A seek landed while this was in flight; its window is stale.
      if (generation !== this.generation) return

      // Offsets arrive on the stored timeline; everything past this point
      // works in playhead positions.
      const historyMessages = data.history.map(m => this.toDisplay(m))
      const messages = data.messages.map(m => this.toDisplay(m))

      for (const message of historyMessages) this.appendMessage(message)
      this.pending.push(...messages)
      // Chat outran what one window can carry. Saying so where it happened
      // beats a silent gap in the busiest moment of the show.
      if (data.truncated && messages.length > 0) {
        this.pending.push({
          note: 'Chat was too busy to show every message here.',
          offset: messages[messages.length - 1].offset,
        })
      }
      this.fetchedThrough = data.next_from
      this.endOfChat = data.end_of_chat

      this.hideStatus()
      this.drain(this.lastTime)
      // The flag, not the array that used to shadow it here: an array is
      // always truthy, so the placeholder was being considered on every poll
      // instead of only on an initial load or a seek.
      if (history) this.showPlaceholderIfEmpty()
    } finally {
      // Left alone when superseded: the newer generation owns the flag, and
      // clearing it here would let a prefetch fire alongside its request.
      if (generation === this.generation) this.loading = false
    }
  }

  private toDisplay(message: ReplayMessage): ReplayMessage {
    return this.offsetAdjust === 0
      ? message
      : {...message, offset: message.offset + this.offsetAdjust}
  }

  private maybePrefetch() {
    if (this.loading || this.endOfChat) return
    if (this.fetchedThrough - this.storedPosition(this.lastTime) > this.PREFETCH_LEAD) return

    this.fetchWindow(this.fetchedThrough, false, this.generation)
  }

  // Moves every message the playhead has now reached out of the buffer and
  // into the pane.
  private drain(time: number) {
    if (this.seeking) return

    let due = 0
    while (due < this.pending.length && this.pending[due].offset <= time) due++
    if (due === 0) return

    for (const item of this.pending.splice(0, due)) {
      if (isNote(item)) this.appendNote(item.note)
      else this.appendMessage(item)
    }
    this.trim()
    if (this.pinned) this.scrollToBottom()
  }

  private appendMessage(message: ReplayMessage) {
    this.placeholder?.remove()
    this.placeholder = null

    const row = this.cloneTemplate(this.messageTemplate)
    row.dataset.messageId = message.id
    row.dataset.login = message.login

    const name = row.querySelector('[data-chat-name]') as HTMLElement
    // Same palette and same key as live chat, so a regular keeps the colour
    // the audience knew them by during the show.
    name.classList.add(this.NAME_COLORS[message.user_id % this.NAME_COLORS.length])
    name.textContent = message.login

    if (!message.staff) row.querySelector('[data-chat-staff-badge]')?.remove()

    const body = row.querySelector('[data-chat-body]') as HTMLElement
    body.textContent = message.body

    this.messagesContainer.appendChild(row)
  }

  private appendNote(text: string) {
    const row = this.cloneTemplate(this.systemMessageTemplate)
    row.textContent = text
    this.messagesContainer.appendChild(row)
  }

  private showPlaceholderIfEmpty() {
    if (this.messagesContainer.children.length > 0 || this.pending.length > 0) return

    this.placeholder = this.cloneTemplate(this.systemMessageTemplate)
    this.placeholder.textContent = this.endOfChat && this.lastTime === 0
      ? 'No chat was recorded for this stream.'
      : 'No chat around this point in the video.'
    this.messagesContainer.appendChild(this.placeholder)
  }

  // Keeps the pane bounded. Trimming above someone who has scrolled up would
  // shift what they're reading, so their scroll position is compensated.
  private trim() {
    const container = this.messagesContainer
    while (container.children.length > this.MAX_RENDERED) {
      const first = container.firstElementChild as HTMLElement
      const height = first.offsetHeight
      first.remove()
      if (!this.pinned) container.scrollTop = Math.max(0, container.scrollTop - height)
    }
  }

  private scrollToBottom() {
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight
  }

  private cloneTemplate(template: HTMLTemplateElement): HTMLElement {
    return (template.content.firstElementChild as HTMLElement).cloneNode(true) as HTMLElement
  }

  private showStatus(text: string) {
    if (!this.statusLine) return
    this.statusLine.textContent = text
    this.statusLine.hidden = false
  }

  private hideStatus() {
    if (!this.statusLine) return
    this.statusLine.textContent = ''
    this.statusLine.hidden = true
  }

  // Hiding the pane stops the requests as well; showing it again refills from
  // wherever the video has got to in the meantime.
  toggleChat() {
    const isVisible = !this.chatSidebar.hidden
    this.chatSidebar.hidden = isVisible
    this.toggleButton.hidden = !isVisible

    if (isVisible) {
      this.generation++
      this.pending = []
    } else {
      this.reset(this.currentTime())
    }
  }
}
